// Nova conversa / Editar grupo (mockups "Modal Nova Conversa A" e "Modal Novo
// Chat Direto A"): segmented Direta / Grupo. Direta lista os colegas (online,
// cargo · fila, "Conversa existente" abre a que já existe). Grupo: nome,
// chips de participantes, picker com checkbox e "Adicionar toda a fila X".
// POST/PUT /chats como antes (users, title, description, groupImage, isGroup).
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { makeStyles, alpha } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";
import GroupIcon from "@material-ui/icons/Group";
import SearchIcon from "@material-ui/icons/Search";
import ForumIcon from "@material-ui/icons/Forum";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { initialsOf, avatarColorFor, findDirectChat } from "./chatRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const cyanText = stateText(theme, AEROS.cyan, "info");
  const borderInput = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)";
  return {
    paper: { width: 560, maxWidth: "calc(100vw - 32px)", margin: 16, maxHeight: "calc(var(--vh-full) - 32px)" },
    segmented: { display: "flex", border: `1px solid ${borderInput}`, borderRadius: 10, overflow: "hidden", fontSize: 12.5, fontWeight: 600 },
    segment: { flex: 1, textAlign: "center", padding: "9px 0", color: theme.palette.text.secondary, background: "transparent", border: "none", cursor: "pointer", font: "inherit", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, "& .MuiSvgIcon-root": { fontSize: 16 } },
    segmentOn: { backgroundColor: "rgba(160,68,255,0.2)", color: purpleText },
    labelRow: { display: "flex", alignItems: "center", marginBottom: 6 },
    count: { color: theme.palette.aeros.textTertiary, fontWeight: 500 },
    linkBtn: { marginLeft: "auto", fontSize: 11.5, fontWeight: 600, color: cyanText, background: "none", border: "none", cursor: "pointer", font: "inherit", "&:hover": { textDecoration: "underline" } },
    chips: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${borderInput}`, borderRadius: 10, padding: "7px 10px", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },
    chip: { display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, borderRadius: 99, padding: "3px 9px 3px 3px", backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)", border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`, color: theme.palette.text.primary, "& .MuiSvgIcon-root": { fontSize: 12, color: theme.palette.text.secondary, cursor: "pointer" } },
    chipAvatar: { width: 18, height: 18, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800, color: "#07080D" },
    chipInput: { flex: 1, minWidth: 120, fontSize: 12.5, color: theme.palette.text.primary, "& input::placeholder": { color: theme.palette.aeros.textTertiary, opacity: 1 }, "& input": { padding: "3px 4px" } },
    picker: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`, borderRadius: 12, marginTop: 8, overflow: "hidden", maxHeight: 260, overflowY: "auto", ...theme.scrollbarStyles },
    userRow: { display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`, cursor: "pointer", width: "100%", background: "none", border: "none", borderBottomStyle: "solid", font: "inherit", color: "inherit", textAlign: "left", "&:hover": { backgroundColor: "rgba(160,68,255,0.06)" } },
    userRowOn: { backgroundColor: "rgba(160,68,255,0.06)" },
    checkbox: { width: 16, height: 16, borderRadius: 4, border: "1.5px solid #5B6376", display: "flex", alignItems: "center", justifyContent: "center", color: "#07080D", fontSize: 10, fontWeight: 800, flex: "none" },
    checkboxOn: { borderColor: "transparent", background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})` },
    avatarWrap: { position: "relative", flex: "none" },
    avatar: { width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#07080D" },
    avatarLg: { width: 34, height: 34, fontSize: 11 },
    dot: { position: "absolute", bottom: 0, right: 0, width: 8, height: 8, borderRadius: "50%", boxShadow: `0 0 0 2px ${theme.palette.aeros.surface}` },
    name: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    role: { fontSize: 11, color: theme.palette.text.secondary },
    queuePill: { fontSize: 10.5, fontWeight: 600, color: theme.palette.text.secondary, backgroundColor: isLight ? "rgba(15,20,40,0.06)" : "rgba(255,255,255,0.06)", borderRadius: 5, padding: "1px 7px", whiteSpace: "nowrap" },
    section: { fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: theme.palette.aeros.textTertiary, padding: "6px 10px 4px" },
    dmRow: { display: "flex", alignItems: "center", gap: 11, padding: "9px 10px", borderRadius: 10, cursor: "pointer", width: "100%", background: "none", border: "none", font: "inherit", color: "inherit", textAlign: "left", "&:hover": { backgroundColor: "rgba(160,68,255,0.08)" }, "& > .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.aeros.textTertiary } },
    existing: { fontSize: 10.5, fontWeight: 600, color: cyanText, backgroundColor: alpha(AEROS.cyan, 0.1), borderRadius: 99, padding: "2px 9px", display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", "& .MuiSvgIcon-root": { fontSize: 12 } },
    searchBox: { backgroundColor: theme.palette.aeros.surface, border: `1px solid ${borderInput}`, borderRadius: 10, padding: "4px 12px", fontSize: 13, color: theme.palette.text.primary, display: "flex", gap: 8, alignItems: "center", width: "100%", "& .MuiSvgIcon-root": { fontSize: 17, color: theme.palette.text.secondary }, "&.Mui-focused": { borderColor: AEROS.cyan, boxShadow: "0 0 0 3px rgba(34,211,238,0.15)" } },
    footNote: { padding: "12px 24px 16px", borderTop: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`, fontSize: 11.5, color: theme.palette.aeros.textTertiary, display: "flex", alignItems: "center", gap: 5, "& .MuiSvgIcon-root": { fontSize: 14 } },
    dmList: { display: "flex", flexDirection: "column", maxHeight: 320, overflowY: "auto", ...theme.scrollbarStyles },
  };
});

const roleLabel = (u) => (u.profile === "admin" ? "Administrador" : u.profile === "supervisor" ? "Supervisor" : "Atendente");

export default function NewChatModal({ open, mode = "new", chat, chats, onlineUsers, onClose, onSaved }) {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user: me } = useContext(AuthContext);
  const [kind, setKind] = useState("direct");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  // guarda síncrona: dois cliques antes do re-render criavam duas conversas
  const busyRef = useRef(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setSaving(false);
    api
      .get("/users/list")
      .then(({ data }) => setUsers((Array.isArray(data) ? data : []).filter((u) => Number(u.id) !== Number(me.id))))
      .catch((err) => toastError(err));
    if (isEdit && chat) {
      setKind("group");
      setTitle(chat.title || "");
      setSelected((chat.users || []).filter((u) => Number(u.userId) !== Number(me.id)).map((u) => ({ id: u.user?.id ?? u.userId, name: u.user?.name || "" })));
    } else {
      setKind("direct");
      setTitle("");
      setSelected([]);
    }
  }, [open, isEdit, chat, me.id]);

  const online = useMemo(() => new Set((onlineUsers || []).filter((u) => u.online).map((u) => Number(u.id))), [onlineUsers]);
  const term = search.trim().toLowerCase();
  const filtered = users.filter((u) => !term || u.name.toLowerCase().includes(term));
  const queues = useMemo(() => {
    const map = new Map();
    users.forEach((u) => (u.queues || []).forEach((q) => map.set(q.id, q.name)));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [users]);
  const isSel = (u) => selected.some((s) => Number(s.id) === Number(u.id));
  const toggle = (u) => setSelected((prev) => (isSel(u) ? prev.filter((s) => Number(s.id) !== Number(u.id)) : [...prev, { id: u.id, name: u.name }]));
  const addQueue = (q) => {
    const add = users.filter((u) => (u.queues || []).some((x) => x.id === q.id) && !isSel(u)).map((u) => ({ id: u.id, name: u.name }));
    setSelected((prev) => [...prev, ...add]);
  };

  const openDirect = async (u) => {
    if (busyRef.current) return;
    const existing = findDirectChat(chats, me.id, u.id);
    if (existing) {
      onClose();
      onSaved(existing, true);
      return;
    }
    busyRef.current = true;
    setSaving(true);
    try {
      const { data } = await api.post("/chats", { users: [{ id: u.id, name: u.name }], title: "", description: "", groupImage: "", isGroup: false });
      onClose();
      onSaved(data);
    } catch (err) {
      toastError(err);
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const saveGroup = async () => {
    if (!title.trim()) {
      toast.error(i18n.t("chatIndex.modal.nameRequired"));
      return;
    }
    if (selected.length === 0) {
      toast.error(i18n.t("chatIndex.modal.membersRequired"));
      return;
    }
    setSaving(true);
    try {
      const body = { users: selected, title: title.trim(), description: chat?.description || "", groupImage: chat?.groupImage || "", isGroup: true };
      const { data } = isEdit ? await api.put(`/chats/${chat.id}`, body) : await api.post("/chats", body);
      onClose();
      onSaved(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const onlineFirst = [...filtered].sort((a, b) => Number(online.has(Number(b.id))) - Number(online.has(Number(a.id))));
  const groupDm = { on: onlineFirst.filter((u) => online.has(Number(u.id))), off: onlineFirst.filter((u) => !online.has(Number(u.id))) };

  return (
    <Dialog open={Boolean(open)} onClose={saving ? undefined : onClose} maxWidth={false} classes={{ paper: classes.paper }} scroll="paper">
      <div className={m.form}>
        <div className={m.header}>
          <div>
            <div className={m.title}>{i18n.t(isEdit ? "chatIndex.modal.editTitle" : "chatIndex.modal.title")}</div>
            <div className={m.subtitle}>{i18n.t(isEdit ? "chatIndex.modal.editSubtitle" : kind === "direct" ? "chatIndex.modal.subtitleDirect" : "chatIndex.modal.subtitle")}</div>
          </div>
          <IconButton className={m.closeButton} onClick={onClose} disabled={saving} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </div>

        <div className={m.body} style={{ gap: kind === "direct" ? 12 : 16 }}>
          {!isEdit && (
            <div className={classes.segmented} role="tablist">
              {[
                ["direct", PersonIcon, "chatIndex.modal.direct"],
                ["group", GroupIcon, "chatIndex.modal.group"],
              ].map(([k, Icon, label]) => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} className={`${classes.segment} ${kind === k ? classes.segmentOn : ""}`} onClick={() => setKind(k)}>
                  <Icon /> {i18n.t(label)}
                </button>
              ))}
            </div>
          )}

          {kind === "direct" ? (
            <>
              <InputBase className={classes.searchBox} autoFocus placeholder={i18n.t("chatIndex.modal.searchPerson")} value={search} onChange={(e) => setSearch(e.target.value)} startAdornment={<SearchIcon />} />
              <div className={classes.dmList}>
                {[["on", "chatIndex.modal.onlineSection"], ["off", "chatIndex.modal.offlineSection"]].map(([k, label]) =>
                  groupDm[k].length === 0 ? null : (
                    <React.Fragment key={k}>
                      <div className={classes.section}>{i18n.t(label)}</div>
                      {groupDm[k].map((u) => {
                        const existing = findDirectChat(chats, me.id, u.id);
                        return (
                          <button type="button" key={u.id} className={classes.dmRow} onClick={() => openDirect(u)} disabled={saving}>
                            <div className={classes.avatarWrap}>
                              <div className={`${classes.avatar} ${classes.avatarLg}`} style={{ backgroundColor: avatarColorFor(u.id) }}>{initialsOf(u.name)}</div>
                              <span className={classes.dot} style={{ backgroundColor: k === "on" ? AEROS.success : "#5B6376" }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className={classes.name}>{u.name}</div>
                              <div className={classes.role}>{roleLabel(u)}{u.queues?.length ? ` · ${u.queues.map((q) => q.name).join(", ")}` : ""}</div>
                            </div>
                            {existing && <span className={classes.existing}><ForumIcon /> {i18n.t("chatIndex.modal.existing")}</span>}
                            <ChevronRightIcon />
                          </button>
                        );
                      })}
                    </React.Fragment>
                  )
                )}
                {filtered.length === 0 && <div className={classes.section} style={{ textTransform: "none", letterSpacing: 0 }}>{i18n.t("chatList.noUserFound")}</div>}
              </div>
            </>
          ) : (
            <>
              <div>
                <label className={`${m.fieldLabel} ${m.fieldLabelFocus}`} htmlFor="chat-group-name">{i18n.t("chatIndex.modal.name")}</label>
                <TextField id="chat-group-name" variant="outlined" className={m.input} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={i18n.t("chatIndex.modal.namePlaceholder")} autoFocus />
              </div>
              <div>
                <div className={classes.labelRow}>
                  <label className={m.fieldLabel} style={{ marginBottom: 0 }}>
                    {i18n.t("chatIndex.modal.members")} <span className={classes.count}>· {i18n.t("chatIndex.modal.selected", { count: selected.length })}</span>
                  </label>
                  {queues.length > 0 && (
                    <button type="button" className={classes.linkBtn} onClick={() => addQueue(queues[0])}>
                      {i18n.t("chatIndex.modal.addQueue", { queue: queues[0].name })}
                    </button>
                  )}
                </div>
                <div className={classes.chips}>
                  {selected.map((s) => (
                    <span key={s.id} className={classes.chip}>
                      <span className={classes.chipAvatar} style={{ backgroundColor: avatarColorFor(s.id) }}>{initialsOf(s.name)}</span>
                      {(s.name || "").split(" ")[0]}
                      <CloseIcon onClick={() => setSelected((prev) => prev.filter((x) => x.id !== s.id))} />
                    </span>
                  ))}
                  <InputBase className={classes.chipInput} placeholder={i18n.t("chatIndex.modal.searchPerson")} value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className={classes.picker}>
                  {filtered.map((u) => {
                    const on = isSel(u);
                    return (
                      <button type="button" key={u.id} className={`${classes.userRow} ${on ? classes.userRowOn : ""}`} onClick={() => toggle(u)}>
                        <span className={`${classes.checkbox} ${on ? classes.checkboxOn : ""}`}>{on ? "✓" : ""}</span>
                        <div className={classes.avatarWrap}>
                          <div className={classes.avatar} style={{ backgroundColor: avatarColorFor(u.id) }}>{initialsOf(u.name)}</div>
                          <span className={classes.dot} style={{ backgroundColor: online.has(Number(u.id)) ? AEROS.success : "#5B6376" }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className={classes.name}>{u.name}</div>
                          <div className={classes.role}>{roleLabel(u)}</div>
                        </div>
                        {u.queues?.[0] && <span className={classes.queuePill}>{u.queues[0].name}</span>}
                      </button>
                    );
                  })}
                  {filtered.length === 0 && <div className={classes.section} style={{ textTransform: "none", letterSpacing: 0, padding: 12 }}>{i18n.t("chatList.noUserFound")}</div>}
                </div>
              </div>
            </>
          )}
        </div>

        {kind === "direct" ? (
          <div className={classes.footNote}>
            <InfoOutlinedIcon /> {i18n.t("chatIndex.modal.directHint")}
          </div>
        ) : (
          <div className={m.footer}>
            <div className={m.footerActions}>
              <Button variant="outlined" className={m.cancelButton} onClick={onClose} disabled={saving}>{i18n.t("chatIndex.cancel")}</Button>
              <Button variant="contained" color="primary" className={m.submitButton} startIcon={<ForumIcon />} onClick={saveGroup} disabled={saving}>
                {i18n.t(isEdit ? "chatIndex.modal.save" : "chatIndex.modal.create")}
                {saving && <CircularProgress size={22} className={m.buttonProgress} />}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
